import * as React from 'react';
import styled from 'styled-components/macro';
import { theme } from 'lib/style';
import CloseIcon from 'lib/images/CloseIcon';
import { Modal, CheckboxInput } from 'lib/components';
import { useState } from 'react';
import { useHistory } from 'react-router';
import { successToast } from 'lib/components/toasts/success';
import { useBillingContext, regenerateJwtToken, useAuth } from 'lib/context';
import dayjs from 'dayjs';
import { PackageWithAdditionalInfo } from 'lib/const/PackageAdditionalInfo';
import { addOrUpdatePlanSubscription } from 'lib/api/paymentApi';
import { STANDARD_DATE_FORMAT } from 'lib/const/DateFormat';
import { Button } from 'react-covideo-common';

const ModalItem = styled.div`
  box-sizing: border-box;
  width: 592px;
  padding: 32px;
  min-height: 232px;
  background-color: ${theme.palette.white};
`;

const Header = styled.div`
  display: flex;
  flex-direction: row;
  height: 24px;
  h2 {
    font-weight: bold;
    font-size: 18px;
    line-height: 24px;
    color: ${theme.palette.black_1_100};
    margin: 0;
  }
  align-items: center;
  justify-content: space-between;
  margin: 0;
  padding: 0;
  color: ${theme.palette.black_1_100};
`;

const CloseButtonWrapper = styled.label`
  display: flex;
  align-items: center;
  margin: 0;
  padding: 0;
  color: ${theme.palette.coal};
  &:hover {
    cursor: pointer;
  }
`;

const Content = styled.div``;

const ButtonsWrapper = styled.section`
  display: flex;
  justify-content: flex-end;
  margin-top: 32px;
  gap: 8px;
`;

const Info = styled.div`
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: ${theme.palette.gray100};
  margin: 32px 0;
`;

const Alert = styled.label`
  display: flex;
  color: ${theme.palette.buttonDanger};
`;

const ReasonContainer = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 16px;
  padding-top: 20px;
`;

const ReasonItem = styled.div`
  display: flex;
  align-items: center;
  column-gap: 10px;
`;

const Input = styled.input`
  padding: 8px 12px;
  box-sizing: border-box;
  width: 100%;
  height: 40px;
  border-radius: 4px;
  background: #ffffff;
  border: 1px solid #c8cfd9;
  font-size: 16px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.5;
  letter-spacing: normal;
  color: ${theme.palette.black_1_100};
  &:focus {
    outline: 0;
  }
`;

const reasonOptions = [
  'It doesn’t work how I expected',
  'Technical problems',
  'Don’t need it anymore',
  'Too expensive',
];

type Props = {
  handleModalClose: () => void;
  currentPackageDetails: PackageWithAdditionalInfo;
  newPackageDetails: PackageWithAdditionalInfo;
};

export const ModalDowngradeCancelPackage = ({
  handleModalClose,
  newPackageDetails,
  currentPackageDetails,
}: Props) => {
  const {
    state: {
      subscription: { currentPeriodEnd },
    },
  } = useBillingContext();
  const { setJwt } = useAuth();

  const activeEnd = currentPeriodEnd
    ? dayjs(new Date(currentPeriodEnd * 1000)).format(STANDARD_DATE_FORMAT)
    : '--';
  const history = useHistory();
  const [reason, setReason] = useState<any>('');

  const isDowngrading = newPackageDetails?.id !== currentPackageDetails?.id;

  const title = isDowngrading
    ? `Downgrade to ${newPackageDetails.title} Plan?`
    : `Cancel ${newPackageDetails.title} Plan?`;
  const text = isDowngrading
    ? `Are you sure you want to downgrade to ${newPackageDetails.title} Plan?`
    : `Are you sure you want to cancel to Free Plan?`;
  const boldedText = isDowngrading
    ? `You will no longer have ${currentPackageDetails.title} features.`
    : `You will no longer have ${currentPackageDetails.title} features.`;
  const keepText = isDowngrading
    ? `No, Keep ${currentPackageDetails.title} Plan`
    : `No, Keep ${currentPackageDetails.title} Plan`;
  const cancelText = `Yes, Cancel Subscription`;
  const alertText = isDowngrading
    ? `What’s the reason you’re downgrading subscription?`
    : `What’s the reason you’re canceling subscription?`;
  const stillActiveText = `${currentPackageDetails.title} Plan will stay active until ${activeEnd}.`;
  const successText = isDowngrading
    ? `You’ve successfully downgraded to ${newPackageDetails.title} Plan!`
    : `You’ve successfully canceled to Free Plan!`;

  const downgradeCancelPackage = async () => {
    //downgrade/cancel package
    const payload = {
      packageId: isDowngrading ? newPackageDetails.id : 1,
      cancellationReason: reason,
    };
    await addOrUpdatePlanSubscription(payload);
    await regenerateJwtToken({ setJwt });

    //TODO: downgrade/cancel package
    successToast({ title: successText, additionalInfo: stillActiveText });
    history.push({ pathname: '/billing' });
  };

  return (
    <Modal>
      <ModalItem>
        <Header>
          <h2>{title}</h2>
          <CloseButtonWrapper>
            <CloseIcon
              width={'24px'}
              height={'24px'}
              onClick={handleModalClose}
            />
          </CloseButtonWrapper>
        </Header>
        <Content>
          <Info>
            {text} <b>{boldedText}</b>
            <br />
            {stillActiveText}
          </Info>
          <Alert>{alertText}</Alert>
          <ReasonContainer>
            {reasonOptions.map((reasonItem, i) => (
              <ReasonItem key={`reason-item-${i}`}>
                <CheckboxInput
                  blueCheck={true}
                  checkGroupIndicator={false}
                  checked={reason == reasonItem}
                  onClick={(event: React.ChangeEvent<HTMLInputElement>) => {
                    event.stopPropagation();
                  }}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    event.stopPropagation();
                    let { checked } = event.target as HTMLInputElement;
                    if (checked) setReason(reasonItem);
                    else setReason('');
                  }}
                />
                {reasonItem}
              </ReasonItem>
            ))}
            <ReasonItem>
              <Input
                type='text'
                value={reasonOptions.includes(reason) ? '' : reason}
                placeholder='Or enter your own'
                onChange={event => {
                  setReason(event.target.value);
                }}
              />
            </ReasonItem>
          </ReasonContainer>
        </Content>
        <ButtonsWrapper>
          <Button
            variant='secondary'
            text={cancelText}
            onClick={downgradeCancelPackage}
            disabled={!reason.length}
          />
          <Button text={keepText} onClick={handleModalClose} />
        </ButtonsWrapper>
      </ModalItem>
    </Modal>
  );
};
